import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Autocomplete from "../common/Autocomplete";
import { getLicenseLabel } from "../../utils/credentialsUtils";
import { upsertLicense, removeLicense, setPrimaryLicense } from "../../utils/licenseUtils";

// Multi-state medical license manager. Used in both the profile edit modal
// (ModalProfileEdit) and the onboarding wizard (ModalWizard) wherever a
// credentialed provider (MD/DO/DPM/ARNP/PA) records their license(s).
//
// Controlled: the parent owns the licenses array (`value`) and persists it via
// `onChange`. The parent is also responsible for mirroring the primary number
// into `custom.licenseNumber` (see getPrimaryLicenseNumber in licenseUtils) so
// downstream readers keep working. This component does NOT touch practiceState.
//
// Props:
//   value      - [{ state, licenseNumber, primary }]
//   onChange   - (nextArray) => void
//   credential - credential code (MD/DO/...) used only for the section label
//   states     - [{ code, name }] (the shared US states list from useNoteType)
//   disabled   - optional
function MedicalLicenseManager({ value = [], onChange, credential, states = [], disabled = false }) {
  const { t } = useTranslation();

  // Draft "add" row state. The Autocomplete is remounted via `resetKey` after a
  // successful add so the picker visibly clears back to its placeholder.
  const [draftState, setDraftState] = useState("");
  const [draftNumber, setDraftNumber] = useState("");
  const [resetKey, setResetKey] = useState(0);

  const stateOptions = useMemo(() => states.map((s) => ({ key: s.code, label: s.name })), [states]);

  const stateName = (code) => states.find((s) => s.code === code)?.name || code || "—";

  const canAdd = !disabled && !!draftState && draftNumber.trim().length > 0;

  const handleAdd = () => {
    if (!canAdd) return;
    onChange(upsertLicense(value, { state: draftState, licenseNumber: draftNumber }));
    setDraftState("");
    setDraftNumber("");
    setResetKey((k) => k + 1); // remount Autocomplete to clear the field
  };

  const handleNumberKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="row-form medical-license-row">
      <label>{getLicenseLabel(credential)}</label>
      <div className="medical-license-manager">
        <div className="mlm-add-row">
          <div className="mlm-state">
            <Autocomplete
              key={resetKey}
              data={stateOptions}
              disabled={disabled}
              placeholder={t("common.medicalLicense.statePlaceholder")}
              inputProps={{ "aria-label": t("common.medicalLicense.statePlaceholder") }}
              onSelect={(opt) => setDraftState(opt?.key || "")}
            />
          </div>
          <input
            type="text"
            className="mlm-number"
            value={draftNumber}
            disabled={disabled}
            aria-label={t("common.medicalLicense.numberPlaceholder")}
            placeholder={t("common.medicalLicense.numberPlaceholder")}
            onChange={(e) => setDraftNumber(e.target.value)}
            onKeyDown={handleNumberKeyDown}
          />
          <button type="button" className="btn btn-primary mlm-add-btn" disabled={!canAdd} onClick={handleAdd}>
            {t("common.medicalLicense.add")}
          </button>
        </div>

        {value.length > 0 && (
          <ul className="mlm-list">
            {value.map((lic) => (
              <li key={lic.state || lic.licenseNumber} className="mlm-row">
                <span className="mlm-row-state">{stateName(lic.state)}</span>
                <span className="mlm-row-number">{lic.licenseNumber}</span>
                {lic.primary ? (
                  <span className="mlm-badge">{t("common.medicalLicense.primary")}</span>
                ) : (
                  <button
                    type="button"
                    className="mlm-link"
                    disabled={disabled}
                    onClick={() => onChange(setPrimaryLicense(value, lic.state))}
                  >
                    {t("common.medicalLicense.setPrimary")}
                  </button>
                )}
                <button
                  type="button"
                  className="mlm-remove"
                  aria-label={t("common.medicalLicense.removeAria")}
                  disabled={disabled}
                  onClick={() => onChange(removeLicense(value, lic.state))}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="mlm-hint">{t("common.medicalLicense.hint")}</div>
      </div>
    </div>
  );
}

export default MedicalLicenseManager;
